// Persist config for useTripStore.ts, split into its own file so the store
// creator function stays under the package's max-lines-per-function limit.
export const TRIP_STORE_PERSIST_NAME = 'trip-designer-storage';

// The hotel fields kept across a page refresh, the same for both apps: a
// rehydrated hotel is read without a fresh fetch, so every field a day row,
// a badge or a gate reads must be here.
function pickHotel(h: any) {
  return {
    id: h.id,
    // isOwnProduct gating (partner vs own product) on DMC.
    organizationId: h.organizationId,
    koediaId: h.koediaId,
    displayName: h.name ?? h.displayName,
    address: h.address,
    currency: h.currency,
    lat: h.lat,
    lon: h.lon,
    city: h.city,
    // KD-3635 — the city id is what the conflict badge matches on;
    // dropping it here would re-flag a same-city hotel after a refresh.
    cityId: h.cityId,
    region: h.region,
    country: h.country,
    imageUrl: h.imageUrl || h.primaryAttachment?.imageUrl,
    stars: h.stars,
    styles: h.styles,
    instantAvailability: h.instantAvailability,
    hasToRequest: h.hasToRequest,
    isIncluded: h.isIncluded,
    promotionsCount: h.promotions?.length ?? h.promotionsCount,
    organization: h.organization,
    price: h.price,
    // KD-3722: the promotions that price belongs with.
    priceWithoutPromo: h.priceWithoutPromo,
    appliedPromotions: h.appliedPromotions,
    // Persisted so the day row can still explain a stay short of the
    // hotel's minimum after a refresh rehydrates the store.
    minimumStay: h.minimumStay,
  };
}

export function partializeTripState(state: any) {
  return {
    days: state.days.map((day: any) => {
      const prunedDay = { ...day };
      delete prunedDay.experience;
      delete prunedDay.hotel;

      if (prunedDay.selectedRoom) {
        const cleanRoom = { ...prunedDay.selectedRoom };
        // Every room on offer: by far the largest field. `allRooms` (the
        // rooms picked) stays, multi-room pricing reads it.
        delete cleanRoom.availabilityData;
        prunedDay.selectedRoom = cleanRoom;
      }
      // Keep localStorage under its quota (big DMC trips hit it): drop what
      // is only shown when fetched again.
      if (prunedDay.experiencePricing) {
        const pricing = { ...prunedDay.experiencePricing };
        delete pricing.policies;
        prunedDay.experiencePricing = pricing;
      }
      if (Array.isArray(prunedDay.selectedExtras)) {
        prunedDay.selectedExtras = prunedDay.selectedExtras.map(
          ({ description, ...extra }: any) => extra,
        );
      }
      return prunedDay;
    }),
    experiences: Object.fromEntries(
      Object.entries(state.experiences).map(([id, exp]: [string, any]) => {
        const prunedExp = { ...exp };
        delete prunedExp.description;
        delete prunedExp.longDescription;
        delete prunedExp.extraRemarks;
        delete prunedExp.includedServices;
        delete prunedExp.excludedServices;
        delete prunedExp.cancellationPolicies;
        delete prunedExp.translations;
        return [id, prunedExp];
      }),
    ),
    hotels: Object.fromEntries(
      Object.entries(state.hotels).map(([id, h]: [string, any]) => [
        id,
        pickHotel(h),
      ]),
    ),
    tripId: state.tripId,
    trip: state.trip,
    // filtersSlice (US-046): uiSlice never joins this object, so it never
    // reaches localStorage.
    tripFilters: state.tripFilters,
    libraryFilters: state.libraryFilters,
  };
}

export function createTripStorage() {
  return {
    getItem: (name: string) => localStorage.getItem(name),
    removeItem: (name: string) => localStorage.removeItem(name),
    setItem: (name: string, value: string) => {
      try {
        localStorage.setItem(name, value);
      } catch (error: any) {
        if (error?.name === 'QuotaExceededError') {
          console.error('Trip Designer: localStorage quota exceeded.');
        }
      }
    },
  };
}
